import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Modal, message } from "antd";
import { observer } from "mobx-react-lite";
import { matchPath, useLocation } from "react-router-dom";
import { globalModel } from "../../global/GlobalModel";
import { CustomerServiceChatPanel } from "./CustomerServiceChatPanel";
import { CustomerServiceChatModel } from "./CustomerServiceChatModel";
import { CustomerServiceFloatEntry } from "./CustomerServiceEntry";
import { CustomerServiceChatApi } from "./CustomerServiceChatApi";
import {
  CS_AVATAR_URL,
  CS_DISPLAY_NAME,
  type CsChatChannel,
} from "./CustomerServiceChatTypes";
import "./CustomerServiceChatStyle.css";

export type CustomerServiceChatModalsApi = {
  openCustomerServiceChat: () => void;
};

const Ctx = createContext<CustomerServiceChatModalsApi | null>(null);

export function useCustomerServiceChatModal(): CustomerServiceChatModalsApi {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useCustomerServiceChatModal must be used inside provider");
  }
  return v;
}

const ModalBody = observer(function ModalBody(props: {
  open: boolean;
  channel: CsChatChannel;
  spaceId?: string;
  displayName: string;
}) {
  const model = useMemo(
    () => new CustomerServiceChatModel({ channel: props.channel, spaceId: props.spaceId }),
    [props.open, props.channel, props.spaceId]
  );
  if (!props.open) return null;
  return <CustomerServiceChatPanel model={model} />;
});

export function CustomerServiceChatModalProvider(props: { children: ReactNode }) {
  const { pathname } = useLocation();
  const spaceMatch = matchPath({ path: "/space/:spaceId/*" }, pathname);
  const currentSpaceId = spaceMatch?.params?.spaceId?.trim() || "";

  const [platformOpen, setPlatformOpen] = useState(false);
  const [spaceOpen, setSpaceOpen] = useState(false);
  const [spaceEntryEnabled, setSpaceEntryEnabled] = useState(false);

  useEffect(() => {
    if (!currentSpaceId) {
      setSpaceEntryEnabled(false);
      return;
    }
    let cancelled = false;
    void CustomerServiceChatApi.getSpaceCsEntryEnabled(currentSpaceId).then((enabled) => {
      if (!cancelled) setSpaceEntryEnabled(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [currentSpaceId]);

  const openCustomerServiceChat = useCallback(() => {
    if (!globalModel.isLoggedIn) {
      message.info("请先登录后再联系平台客服");
      globalModel.openLoginModal();
      return;
    }
    setPlatformOpen(true);
  }, []);

  const openSpaceCustomerServiceChat = useCallback(() => {
    if (!globalModel.isLoggedIn) {
      message.info("请先登录后再联系空间客服");
      globalModel.openLoginModal();
      return;
    }
    if (!currentSpaceId) return;
    setSpaceOpen(true);
  }, [currentSpaceId]);

  const api = useMemo(() => ({ openCustomerServiceChat }), [openCustomerServiceChat]);

  const showSpaceFloat = Boolean(currentSpaceId) && spaceEntryEnabled;

  return (
    <Ctx.Provider value={api}>
      {props.children}
      {showSpaceFloat ? (
        <CustomerServiceFloatEntry
          onClick={openSpaceCustomerServiceChat}
          title="空间客服"
        />
      ) : null}
      <Modal
        className="lf-cs-modal"
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <img src={CS_AVATAR_URL} alt="" width={24} height={24} style={{ borderRadius: 4 }} />
            {CS_DISPLAY_NAME}
          </span>
        }
        open={platformOpen}
        footer={null}
        width={850}
        destroyOnHidden
        onCancel={() => setPlatformOpen(false)}
      >
        <ModalBody open={platformOpen} channel="PLATFORM" displayName={CS_DISPLAY_NAME} />
      </Modal>
      <Modal
        className="lf-cs-modal"
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <img src={CS_AVATAR_URL} alt="" width={24} height={24} style={{ borderRadius: 4 }} />
            空间客服
          </span>
        }
        open={spaceOpen}
        footer={null}
        width={850}
        destroyOnHidden
        onCancel={() => setSpaceOpen(false)}
      >
        <ModalBody
          open={spaceOpen}
          channel="SPACE"
          spaceId={currentSpaceId}
          displayName="空间客服"
        />
      </Modal>
    </Ctx.Provider>
  );
}
